import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {AudienceFilter} from 'src/types/audience-filter';
import type {DynamicLabels} from 'src/action-creators/dynamic-labels';
import type {SecurityGroupCreationFields} from 'src/types/groups';

import pick from 'lodash/pick';
import sculpt from 'sculpt';
import invariant from 'invariant';

import {thunkify as flow} from 'src/utils/thunks';
import otherParsers from 'src/api-parsers';
import {camel, snake} from 'src/utils';
import {key, cached, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {rootGroupId} from 'src/utils/groups';
import {type ActiveStatus} from 'src/reducers/groups';
import {
  startBackgroundTask,
  getBackgroundTaskStatus,
} from 'src/action-creators/background-task';


export const RECEIVE: 'groups/receive' = 'groups/receive';
export const RECEIVE_GROUP: 'groups/receiveGroup' = 'groups/receiveGroup';
export const REMOVE_GROUP: 'groups/removeGroup' = 'groups/removeGroup';
export const RECEIVE_SHAREABLE_GROUPS: 'groups/receiveShareableGroups' =
  'groups/receiveShareableGroups';
export const TOGGLE_GROUP_ALL_FILTER: 'groups/toggleGroupAllFilter' =
  'groups/toggleGroupAllFilter';
export const ADD_GROUP_FILTER_RULE: 'groups/addGroupFilterRule' =
  'groups/addGroupFilterRule';
export const EDIT_GROUP_FILTER_RULE: 'groups/editGroupFilterRule' =
  'groups/editGroupFilterRule';
export const DELETE_GROUP_FILTER_RULE: 'groups/deleteGroupFilterRule' =
  'groups/deleteGroupFilterRule';
export const CLEAR_EDITING: 'groups/clearEditing' = 'groups/clearEditing';
export const START_EDITING: 'groups/startEditing' = 'groups/startEditing';
export const START_EDITING_ERROR: 'groups/startEditingError' =
  'groups/startEditingError';
export const SHOW_ALL_EDITING_ERRORS: 'groups/showAllEditingErrors' =
  'groups/showAllEditingErrors';
export const EDIT_GROUP_NAME: 'groups/editGroupName' = 'groups/editGroupName';
export const EDIT_GROUP_PARENT: 'groups/editGroupParent' =
  'groups/editGroupParent';
export const RECEIVE_GROUP_FILTER_STATUS: 'groups/receiveFilterStatus' =
  'groups/receiveFilterStatus';
export const SET_SECURITY_GROUP_CREATION_INPUT_FIELDS =
  'groups/set-security-group-creation-input-fields';

type SetSecurityGroupCreationInputFieldsAction = {
  type: 'groups/set-security-group-creation-input-fields';
  payload: SecurityGroupCreationFields;
};

export type Group = {
  id: string;
  name: string;
  numAgents: number;
  parentSecurityGroupId: string;
  filter: {
    pattern: AudienceFilter;
    totalMembers: number;
  };
};

export type ApiGroup = {
  id: string;
  name: string;
  num_agents: number;
  parent_security_group_id: string | null | undefined;
  filter: {
    pattern: AudienceFilter;
    total_members: number;
  };
};

// returned by workflows/:workflowId/groups/shareable
export type ApiShareableGroup = {
  audience_filter_id: string;
  id: string;
  name: string;
  num_audience_members: number | null | undefined;
  parent_security_group_id: string;
};

export type ShareableGroup = {
  audienceFilterId: string;
  id: string;
  name: string;
  numAudienceMembers: number | null | undefined;
  parentSecurityGroupId: string;
};

// Data structure for PUT, POST
type ApiUpdateGroup = {
  name: string;
  parent_security_group_id: string | null | undefined;
  filter?: {
    pattern: AudienceFilter;
  };
};

export const normalize = (
  group: Group,
  fieldsToTypes?: {
    [field: string]: string;
  },
): ApiUpdateGroup => {
  const writableFieldsOnly = pick(group, ['name', 'parentSecurityGroupId']);

  if (fieldsToTypes) {
    // @ts-expect-error - TS2339 - Property 'filter' does not exist on type 'Pick<Group, "name" | "parentSecurityGroupId">'.
    writableFieldsOnly.filter = {
      pattern: otherParsers.normalize.audienceFilter(
        fieldsToTypes,
        group.filter.pattern,
      ),
    };
  }

  return snake(writableFieldsOnly);
};

export const parse = (group: ApiGroup): Group =>
  camel(
    // @ts-expect-error - TS2349 - This expression is not callable.
    sculpt(group, {
      filter: {
        pattern: {$apply: otherParsers.parse.audienceFilter},
      },
    }),
  );

export type GroupsAction =
  | ReceiveGroupsAction
  | ReceiveSingleGroupAction
  | RemoveGroupAction
  | ReceiveShareableGroupsAction
  | ClearEditingAction
  | StartEditingAction
  | StartEditingErrorAction
  | ShowAllEditingErrorsAction
  | EditGroupNameAction
  | EditGroupParentAction
  | ToggleGroupAllFilterAction
  | AddGroupFilterRuleAction
  | EditGroupFilterRuleAction
  | DeleteGroupFilterRuleAction
  | SetSecurityGroupCreationInputFieldsAction
  | ReceiveGroupFilterStatusAction;

export const setSecurityGroupCreationInputFields = (
  payload: SecurityGroupCreationFields,
): SetSecurityGroupCreationInputFieldsAction => ({
  type: SET_SECURITY_GROUP_CREATION_INPUT_FIELDS,
  payload,
});

export type EditGroup = {
  id: string | null | undefined;
  parentSecurityGroupId: string | null | undefined;
  name: string;
  filter: {
    pattern: AudienceFilter;
  };
};

const emptyGroup: EditGroup = Object.freeze({
  id: null,
  parentSecurityGroupId: null,
  name: '',
  filter: {
    pattern: {and: []},
  },
});

type ReceiveGroupsAction = {
  type: typeof RECEIVE;
  payload: Group[];
};

export const receive = (payload: Group[]): ReceiveGroupsAction => ({
  type: RECEIVE,
  payload,
});

type ReceiveSingleGroupAction = {
  type: typeof RECEIVE_GROUP;
  payload: Group;
};

const receiveGroup = (payload: Group): ReceiveSingleGroupAction => ({
  type: RECEIVE_GROUP,
  payload,
});

type RemoveGroupAction = {
  type: typeof REMOVE_GROUP;
  payload: string;
};

const removeGroup = (payload: string): RemoveGroupAction => ({
  type: REMOVE_GROUP,
  payload,
});

type ReceiveShareableGroupsAction = {
  type: typeof RECEIVE_SHAREABLE_GROUPS;
  payload: ShareableGroup[];
};
const receiveShareableGroups = (
  payload: ShareableGroup[],
): ReceiveShareableGroupsAction => ({
  type: RECEIVE_SHAREABLE_GROUPS,
  payload,
});

type ClearEditingAction = {
  type: typeof CLEAR_EDITING;
};

const clearEditing = (): ClearEditingAction => ({
  type: CLEAR_EDITING,
});

type StartEditingAction = {
  type: typeof START_EDITING;
  payload: EditGroup;
};

export const startEditingGroup = (group: EditGroup): StartEditingAction => ({
  type: START_EDITING,
  payload: group,
});

export type ErrorMessagesByType = {
  [errorName: string]: string[];
};

type StartEditingErrorAction = {
  type: typeof START_EDITING_ERROR;
  payload: ErrorMessagesByType;
  error: true;
};

export const startEditingError = (
  errorMessagesByType: ErrorMessagesByType,
): StartEditingErrorAction => ({
  type: START_EDITING_ERROR,
  payload: errorMessagesByType,
  error: true,
});

export const startEditing =
  (id?: string): ThunkAction<void> =>
  (dispatch: Dispatch, getState: GetState) => {
    dispatch(clearEditing());
    if (id) {
      return dispatch(getGroups()).then(() => {
        const group = getState().groups.data[id];

        if (!group) {
          return dispatch(
            startEditingError({
              notFound: [`Could not get group with id ${String(id)}`],
            }),
          );
        }

        return dispatch(startEditingGroup(group));
      });
    } else {
      const rootId = rootGroupId(getState().groups.data);
      const emptyGroupTemplate = {
        ...emptyGroup,
        parentSecurityGroupId: rootId,
      } as const;
      return dispatch(startEditingGroup(emptyGroupTemplate));
    }
  };

export const startCreatingV2 =
  (): ThunkAction<void> => (dispatch: Dispatch, getState: GetState) => {
    return dispatch(getGroups()).then(() => {
      const rootId = rootGroupId(getState().groups.data);
      return dispatch(
        setSecurityGroupCreationInputFields({
          name: '',
          parent_security_group_id: rootId,
        }),
      );
    });
  };

type ShowAllEditingErrorsAction = {
  type: typeof SHOW_ALL_EDITING_ERRORS;
};

export const showAllEditingErrors = (): ShowAllEditingErrorsAction => ({
  type: SHOW_ALL_EDITING_ERRORS,
});

export const getGroups: () => ThunkAction<unknown> = flow(
  key(() => 'getGroups'),
  cached((response: ApiGroup[]) => receive(response.map(parse)), {ttl: 2000}),
  fetching(),
)(() => reduxApi.get('security/groups'));

export const getShareableGroups: () => ThunkAction<unknown> = flow(
  key(() => 'getShareableGroups'),
  cached(
    (response: ApiShareableGroup[]) =>
      // @ts-expect-error - TS2345 - Argument of type '(thing: any, deep?: boolean, ignoreKeys?: string[]) => any' is not assignable to parameter of type '(value: ApiShareableGroup, index: number, array: ApiShareableGroup[]) => any'.
      receiveShareableGroups(response.map(camel)),
    {ttl: 2000},
  ),
  fetching(),
)(() => reduxApi.get('workflows/groups/shareable'));

export const createGroup =
  (
    group: Group,
    fieldsToTypes?: {
      [key: string]: string;
    },
  ): ThunkAction<ReceiveSingleGroupAction> =>
  (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post('security/groups', normalize(group, fieldsToTypes)),
    ).then((updatedGroup) => dispatch(receiveGroup(parse(updatedGroup))));

export const updateGroup =
  (
    group: Group,
    fieldsToTypes?: {
      [key: string]: string;
    },
  ): ThunkAction<void> =>
  (dispatch: Dispatch) => {
    const {parent_security_group_id, ...groupToUpdate} = normalize(
      group,
      fieldsToTypes,
    );

    return dispatch(
      reduxApi.put(`security/groups/${group.id}`, groupToUpdate),
    ).then((updatedGroup) => dispatch(receiveGroup(parse(updatedGroup))));
  };

export const updateGroupAudienceFilter =
  (
    group: Group,
    fieldsToTypes?: {
      [key: string]: string;
    },
  ): ThunkAction<unknown> =>
  (dispatch: Dispatch) => {
    const {filter} = normalize(group, fieldsToTypes);

    invariant(
      filter,
      'Attempting to update group audience filter that\'s undefined.',
    );

    return dispatch(
      startBackgroundTask(
        `group-${group.id}-audience-filter-create`,
        () =>
          dispatch(
            reduxApi.post(
              `security/groups/${group.id}/audience-filter`,
              filter.pattern,
            ),
          ),
        `security/groups/${group.id}/audience-filter/status`,
      ),
    );
  };

export const getGroupFilterStatus =
  (groupId: string): ThunkAction<unknown> =>
  (dispatch: Dispatch) =>
    dispatch(
      getBackgroundTaskStatus(
        `group-${groupId}-audience-filter-create`,
        `security/groups/${groupId}/audience-filter/status`,
      ),
    );
// TODO (rng) - Handle error result on API status request

export const cancelGroupAudienceFilter =
  (group: Group): ThunkAction<unknown> =>
  (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(`security/groups/${group.id}/audience-filter/cancel`),
    );

export const deleteGroup =
  (groupId: string): ThunkAction<void> =>
  (dispatch: Dispatch) =>
    dispatch(reduxApi.del(`security/groups/${groupId}`)).then(
      () => dispatch(removeGroup(groupId)),
      // NOTE:(diwakersurya) clear editing state after deleting group
      dispatch(clearEditing()),
    );

export const handleSave =
  (
    group: Group,
    router: Record<any, any>,
    nextUrl: string,
    fieldsToTypes?: {
      [field: string]: string;
    },
  ): ThunkAction<unknown> =>
  (dispatch: Dispatch) =>
    group.id
      ? dispatch(updateGroup(group, fieldsToTypes)).then(() =>
          router.push(nextUrl),
        )
      : dispatch(createGroup(group, fieldsToTypes)).then((action) =>
          router.push(`/settings/groups/${action.payload.id}/audience/edit`),
        );

type EditGroupNameAction = {
  type: typeof EDIT_GROUP_NAME;
  payload: {
    name: string;
  };
};

export const editGroupName = (name: string): EditGroupNameAction => ({
  type: EDIT_GROUP_NAME,
  payload: {
    name,
  },
});

type EditGroupParentAction = {
  type: typeof EDIT_GROUP_PARENT;
  payload: {
    parentId: string;
  };
};

export const editGroupParent = (parentId: string): EditGroupParentAction => ({
  type: EDIT_GROUP_PARENT,
  payload: {
    parentId,
  },
});

type ToggleGroupAllFilterAction = {
  type: typeof TOGGLE_GROUP_ALL_FILTER;
  payload: Record<any, any>;
};

export const toggleGroupAllFilter = (): ToggleGroupAllFilterAction => ({
  type: TOGGLE_GROUP_ALL_FILTER,
  payload: Object.freeze({}),
});

type AddGroupFilterRuleAction = {
  type: typeof ADD_GROUP_FILTER_RULE;
  payload: Record<any, any>;
};

export const addGroupFilterRule = (): AddGroupFilterRuleAction => ({
  type: ADD_GROUP_FILTER_RULE,
  payload: Object.freeze({}),
});

type EditGroupFilterRuleAction = {
  type: typeof EDIT_GROUP_FILTER_RULE;
  payload: {
    ruleIndex: number;
    change: {
      [key: string]: any;
    };
    labels: DynamicLabels;
  };
};

export const editGroupFilterRule = (
  ruleIndex: number,
  change: {
    [key: string]: any;
  },
  labels: DynamicLabels,
): EditGroupFilterRuleAction => ({
  type: EDIT_GROUP_FILTER_RULE,
  payload: {
    ruleIndex,
    change,
    labels,
  },
});

type DeleteGroupFilterRuleAction = {
  type: typeof DELETE_GROUP_FILTER_RULE;
  payload: {
    ruleIndex: number;
  };
};

export const deleteGroupFilterRule = (
  ruleIndex: number,
): DeleteGroupFilterRuleAction => ({
  type: DELETE_GROUP_FILTER_RULE,
  payload: {
    ruleIndex,
  },
});

type ReceiveGroupFilterStatusAction = {
  type: typeof RECEIVE_GROUP_FILTER_STATUS;
  payload: {
    groupId: string;
    status: string;
  };
};

const receiveGroupFilterStatus = (
  payload: ActiveStatus,
): ReceiveGroupFilterStatusAction => ({
  type: RECEIVE_GROUP_FILTER_STATUS,
  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type '{ groupId: string; status: string; }'.
  payload,
});
